import { Outlet, Navigate, useLocation } from "react-router-dom";
import { useState } from "react";
import Sidebar from "./Sidebar";
import CaptureDialog from "./CaptureDialog";
import BrainPanel from "./BrainPanel";
import OrgSwitcher from "./OrgSwitcher";
import { useAuth } from "@/lib/auth";
import { useData } from "@/lib/store";
import { Plus, Search, Sparkles } from "lucide-react";

export default function AppLayout() {
  const { user, loading } = useAuth();
  const { loading: dataLoading } = useData();
  const [captureOpen, setCaptureOpen] = useState(false);
  const [brainOpen, setBrainOpen] = useState(false);
  const location = useLocation();
  const greeting = getGreeting();

  if (loading || (user && dataLoading)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface">
        <div className="text-surface-onVariant text-sm">Loading Aevum…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/welcome" state={{ from: location }} replace />;

  return (
    <div className="min-h-screen flex bg-surface text-surface-on">
      <Sidebar onOpenCapture={() => setCaptureOpen(true)} onOpenBrain={() => setBrainOpen(true)} />
      <main className="flex-1 min-w-0">
        {/* Top bar */}
        <div className="px-8 pt-8 pb-2 flex items-center justify-between gap-4">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-3">
              <OrgSwitcher />
              <span className="text-xs uppercase tracking-widest text-surface-onVariant">{new Date().toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}</span>
            </div>
            <h1 className="text-3xl md:text-4xl font-medium text-surface-on tracking-tight mt-2 truncate" data-testid="greeting">
              {greeting}, {user.name.split(" ")[0]}.
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <div className="hidden md:flex items-center gap-2 bg-surface-container rounded-full px-4 py-2.5 w-72">
              <Search className="w-4 h-4 text-surface-onVariant" />
              <input placeholder="Search items…" disabled className="bg-transparent outline-none flex-1 text-sm text-surface-on placeholder:text-surface-onVariant" />
              <span className="text-[10px] text-surface-onVariant tracking-widest">SOON</span>
            </div>
            <button
              data-testid="topbar-ask-btn"
              onClick={() => setBrainOpen(true)}
              className="inline-flex items-center gap-2 rounded-full bg-primary-container text-primary-onContainer font-medium px-4 py-2.5 hover:brightness-95 active:scale-[0.98] transition-[transform,filter] duration-200 ease-md"
            >
              <Sparkles className="w-4 h-4" />
              <span className="hidden sm:inline">Ask</span>
            </button>
          </div>
        </div>

        <div className="px-8 pb-24 pt-4">
          <Outlet context={{ openCapture: () => setCaptureOpen(true), openBrain: () => setBrainOpen(true) }} />
        </div>
      </main>

      {/* FAB */}
      <button
        data-testid="capture-fab"
        onClick={() => setCaptureOpen(true)}
        className="fixed bottom-20 right-6 z-40 rounded-[20px] bg-primary text-primary-foreground shadow-lg hover:shadow-xl active:scale-[0.96] transition-[transform,box-shadow] duration-200 ease-md px-5 py-4 inline-flex items-center gap-2 font-medium"
      >
        <Plus className="w-5 h-5" />
        Capture
      </button>

      <CaptureDialog open={captureOpen} onOpenChange={setCaptureOpen} />
      <BrainPanel open={brainOpen} onOpenChange={setBrainOpen} />
    </div>
  );
}

function getGreeting() {
  const h = new Date().getHours();
  if (h < 5) return "Still up";
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
}
